import LoadingPage from "@components/Loader/LoadingPage";
import { useUserStore } from "@providers/auth";
import { useQuery } from "@tanstack/react-query";
import About from "components/About/About";
import EmailModal from "components/EmailModal/EmailModal";
import EventDetailsSection from "components/EventDetailsSection/EventDetailsSection";
import FaqSection from "components/FaqSection/FaqSection";
import Footer from "components/Footer/Footer";
import LandingNavBar from "components/LandingNavBar/LandingNavBar";
import PaymentSuccess from "components/PaymentSuccess/PaymentSuccess";
import TicketBannerSection from "components/TicketBannerSection/TicketBannerSection";
import TicketTypeSection from "components/TicketTypeSection/TicketTypeSection";
import TicketingModal from "components/TicketingModal/TicketingModal";
import ToolbarSection from "components/ToolbarSection/ToolbarSection";
import { GoIcon } from "components";
import { useAtom } from "jotai";
import type { GetStaticPropsContext, NextPage } from "next";
import { useTranslations } from "next-intl";
import Head from "next/head";
import { useRouter } from "next/router";
import { useEffect, useMemo } from "react";
import { getTickets } from "services/ticket";
import {
  emailModalAtom,
  languageAtom,
  selectedTicketAtom,
  successAtom,
  ticketingModalOpenAtom,
  ticketsAtom,
} from "utils/atoms";
import { Ticket } from "utils/mongo/tickets.model";
import { SelectedTicket } from "../models/selected.model";
import { validateDiscountCode } from "../services/discount";
import { Button, Divider, Text } from "@chakra-ui/react";
import Image from "next/image";

// ::: MAIN COMPONENT :::
export async function getStaticProps({ locale }: GetStaticPropsContext) {
  return {
    props: {
      messages: {
        ...(await import(`../messages/${locale || "en"}.json`)).default,
      },
      locale: locale || "en",
    },
  };
}

const Index: NextPage<{ locale: string }> = ({ locale }) => {
  const t = useTranslations("index");
  const router = useRouter();
  const user = useUserStore((state) => state.user);
  const authenticating = useUserStore((state) => state.authenticating);
  const [, setLang] = useAtom(languageAtom);
  const [, setEmailModal] = useAtom(emailModalAtom);
  const [modalOpen, setTicketingModal] = useAtom(ticketingModalOpenAtom);
  const [selectedTicket, setSelectedTicket] = useAtom(selectedTicketAtom);

  const [, setTickets] = useAtom(ticketsAtom);
  const [success] = useAtom(successAtom);
  const { data: tickets, isLoading } = useQuery(["tickets"], () =>
    getTickets()
  );

  useEffect(() => {
    setLang(locale);
  }, [locale]);

  useEffect(() => {
    if (
      !user ||
      !tickets ||
      !router.isReady ||
      !router.query.checkout ||
      !router.query.item ||
      !router.query.quantity
    )
      return;
    const ticketId = decodeURIComponent(String(router.query.item));
    const ticket = tickets.find((t) => t.id == ticketId);
    if (!ticket) return;
    setTicketingModal(true);
    const quantity = parseInt(
      decodeURIComponent(String(router.query.quantity))
    );
    const discountCode = router.query.code
      ? decodeURIComponent(String(router.query.code))
      : undefined;

    const selectedTicket = new SelectedTicket({ ticket, quantity });
    console.log("setSelectedTicket by query", selectedTicket);
    setSelectedTicket(selectedTicket);

    if (discountCode) {
      validateDiscountCode(
        discountCode,
        selectedTicket,
        setSelectedTicket,
        tickets
      );
    }
  }, [
    user,
    tickets,
    router.isReady,
    router.query.checkout,
    router.query.item,
    router.query.quantity,
    router.query.code,
  ]);

  useEffect(() => {
    if (tickets) setTickets(tickets);
  }, [tickets]);

  const total = useMemo(
    () =>
      (tickets || []).reduce(
        (acc: number, ticket: Ticket) => acc + ticket.total - ticket.sold,
        0
      ),
    [tickets]
  );

  const buyTicket = async () => {
    return setTicketingModal(true);
  };

  if (authenticating || !tickets) return <LoadingPage />;

  return (
    <>
      <Head>
        <title>{t("title")}</title>
      </Head>
      <div className="bg-background w-screen relative">
        <div className="flex flex-col min-h-screen items-stretch bg-center bg-cover bg-no-repeat bg-[url('/images/auth-bg.svg')]">
          <div className="sticky top-0 w-full self-center z-10">
            <LandingNavBar />
          </div>
          {success ? (
            <PaymentSuccess />
          ) : (
            <div className="w-full md:max-w-7xl m-auto flex flex-col p-6 md:pt-16 md:flex-row md:flex-auto items-start justify-end md:items-start md:justify-between">
              {/* Ticket Banner */}
              <TicketBannerSection
                t={t}
                loading={authenticating}
                buyTicket={buyTicket}
              />

              <div className="flex flex-col items-start w-full md:w-1/2 md:pl-16 md:pr-8 ">
                {/* Toolbar & Language Menu */}
                {/* <ToolbarSection /> */}

                {/* Title */}
                <div className="text-4xl md:text-5xl font-bold max-w-xl py-8 md:pb-10 md:py-0">
                  The Astra Club
                </div>

                <Divider background="#A0A0A7" />

                <Text color="neutral.300" fontSize="lg" my="3rem" maxW="75ch">
                  An exclusive club created to reward our closest partners,
                  friends, and early adopters of Moongate. By invitation only.
                  <br />
                  <br />
                  Join our &nbsp;
                  <span className="text-primary font-bold">
                    <a
                      target="_blank"
                      href={"https://discord.com/invite/KpvCUD5NgM"}
                      rel="noopener noreferrer"
                    >
                      Discord
                    </a>
                  </span>
                  &nbsp; server and follow us on &nbsp;
                  <span className="text-primary font-bold">
                    <a
                      target="_blank"
                      href={"https://twitter.com/0xmoongate"}
                      rel="noopener noreferrer"
                    >
                      Twitter
                    </a>
                  </span>
                  &nbsp; to learn more about upcoming drops and requirements!
                </Text>

                <Divider background="#A0A0A7" />

                <Text
                  color="neutral.300"
                  fontSize="lg"
                  mt="2.25rem"
                  mb="1.25rem"
                >
                  Received an invitation? Enter below to claim your membership.
                </Text>

                <Button
                  variant="gradient"
                  flexBasis={0}
                  flexGrow={1}
                  borderRadius="0.25rem"
                  py="4"
                  fontWeight={600}
                  fontSize="1rem"
                  onClick={buyTicket}
                  display="flex"
                  flexDir="row"
                  alignItems="center"
                  justifyContent="center"
                  disabled={authenticating}
                  isLoading={authenticating}
                  width="100%"
                >
                  <>Claim Now</>
                </Button>

                <div className="my-8 md:my-16 w-full">
                  <Text fontSize="2xl" fontWeight={600}></Text>
                </div>

                {/* Spots left */}
                {/*
                <div className="text-3xl font-medium my-4 text-center md:text-left w-full">
                  {t.rich("spotLeft", {
                    total,
                    totalParent: (children) => (
                      <span className="text-primary-300">{children}</span>
                    ),
                  })}
                </div>
                */}

                {/* Event　Details */}

                {/* <EventDetailsSection t={t} /> */}

                {/* Ticket Type */}
                {/*
                <TicketTypeSection
                  t={t}
                  tickets={tickets}
                  isLoading={isLoading}
                />
                */}

                {/* About BZD*/}
                {/* <About title={t("aboutBZD")} content={t("aboutBZDDesc")} /> */}

                {/* About Moongate */}
                {/*
                <About
                  title={t("aboutMoongate")}
                  content={t.rich("aboutMoongateDesc", {
                    total,
                    b: (children) => (
                      <b className="text-primary-300">{children}</b>
                    ),
                    br: () => <br></br>,
                    a: (children) => (
                      <a
                        className="text-primary underline cursor-pointer"
                        href="https://docs.google.com/forms/d/1pEJic-CiNvlpczOVTkP9K0PXrDogB5KxD6O3l7q5FZg/"
                        target="_blank"
                        rel="noreferrer"
                      >
                        {children}
                      </a>
                    ),
                  })}
                />
                */}

                {/* FAQ */}
                {/* <FaqSection t={t} /> */}
              </div>
            </div>
          )}
          <Footer />
        </div>
      </div>

      <TicketingModal />

      {/* email input modal */}
      <EmailModal t={t} />
    </>
  );
};

export default Index;
